import { useMutation } from '@tanstack/react-query';
import { message } from 'antd';
import { AxiosError } from 'axios';
import { API } from 'network/api';
import { MutationConfig } from 'types/request';
import { handleAxiosError } from 'utils/helpers';

type DeleteFileConfig = MutationConfig & {
  successMessage?: string;
  errorMessage?: string;
};

const useDeleteFilesMutation = (config?: DeleteFileConfig) => {
  const mutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) =>
          API.delete(`/documents/${id}`).catch((e) => {
            console.error(`Failed to delete file ${id}`, e);
            return { error: e, id };
          }),
        ),
      );

      const failedDeletions = results
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map((r) => r.reason);

      if (failedDeletions.length > 0) {
        throw new AxiosError(
          `Failed to delete ${failedDeletions.length} files`,
        );
      }
    },
    onSuccess: () => {
      config?.onSuccess?.();
      if (config?.successMessage) message.success(config?.successMessage);
    },
    onError: (error) => {
      config?.onError?.();
      handleAxiosError(
        error,
        config?.errorMessage ?? 'There were some errors deleting the file',
      );
    },
    onSettled: () => {
      config?.onFinish?.();
    },
  });

  return mutation;
};

export default useDeleteFilesMutation;
